/**
 * Narration — the payoff layer (AoD §12), and the owner's explicit ask.
 *
 * The benchmark is the Dwarf Fortress combat log, and what makes that log work
 * is not purple prose: it is that **every clause is a mechanical event,
 * truthfully reported**. The weapon that swung, the thing that stopped it, the
 * armour it turned on, the tissue it reached, the state change it caused. If
 * the log says it, it is true in the sim; if the sim did it, the log can say
 * so. Nothing mechanical happens silently and nothing narrated happened only
 * in the prose.
 *
 * Which makes this module a **renderer over tuples, never a second fiction**.
 * It is handed a `BlowReport` — parties, weapon, defence, the `DamageResult`
 * from M1-03, the `ClockEvent`s from M1-04/05 — and it turns that into an
 * ordered chain of clauses:
 *
 * ```text
 * [declaration]  Gwil swings a bearded axe at the wolf.
 * [defence]      The wolf twists away from it.            (the attack lost)
 *                — or —
 * [contact]      The blow lands.                          (the attack won)
 * [tissue]       A bearded axe opens the flesh of the wolf’s off foreleg.
 * [status]*      The wound bleeds freely. The wolf staggers.
 * ```
 *
 * Three consequences of being a renderer, all load-bearing:
 *
 * - **The tissue clause is not written here.** It comes from the wound table
 *   cell that resolved (M1-02), which is why the anatomical ladder — skin,
 *   muscle, bone, structure, destruction — tracks the wound level for free.
 * - **Selection draws from the battle's RNG stream**, so a replayed fight
 *   produces byte-identical text. The narrator is part of the determinism
 *   contract, not decoration on top of it.
 * - **Text is never archived.** The tuples are; text is re-rendered from
 *   templates on view (AoD §12.3's "narrative depth at telemetry prices"), so
 *   a writing pass rewrites history without migrating it.
 */
import { effectiveArmourValue } from './damage.js';
import { NARRATION_TOKENS } from './wound.js';
/**
 * The sets Annwn needs. Templates avoid pronoun-*subject* constructions for
 * now (M1-02's note), so only `object` and `possessive` are substituted — but
 * `subject` is carried because the M5-06 writing pass will want it and adding
 * it later would mean touching every fixture.
 */
export const PRONOUNS = {
    he: { subject: 'he', object: 'him', possessive: 'his' },
    she: { subject: 'she', object: 'her', possessive: 'her' },
    they: { subject: 'they', object: 'them', possessive: 'their' },
    it: { subject: 'it', object: 'it', possessive: 'its' },
};
/* -------------------------------------------------------------------------- *
 * The grammar
 * -------------------------------------------------------------------------- */
/** The ordered chain of AoD §12.1. `status` repeats; the rest appear once. */
export const CLAUSE_TYPES = ['declaration', 'defence', 'contact', 'tissue', 'status'];
/**
 * What stopped a blow. Named, always — "the blow misses" is the one clause the
 * DF log never settles for.
 *
 * `armour` is not a defence the fighter *made*; it is the blow arriving and
 * finding iron. It reads as a defence clause because that is what the reader
 * needs to know, and because *"the axe turns on his mail"* is a far better line
 * than a silent nothing.
 */
export const DEFENCE_KINDS = ['parry', 'dodge', 'give-ground', 'armour'];
/**
 * Contact pools — the blow got through, and how much of it there was.
 *
 * The `-through` variants are drawn when the blow passed *something* on the way
 * in, and they are why the clothing layer exists: M2-04's kit gives everyone a
 * wool tunic worth no armour value at all, purely so that the log can say what
 * the blade opened before it reached the man. Named separately rather than
 * threading `{armour}` into the plain pools because a template may only reach
 * for data its tuple carries, and a blow at bare skin carries no armour.
 */
export const CONTACT_KINDS = ['solid', 'glancing', 'solid-through', 'glancing-through'];
/* -------------------------------------------------------------------------- *
 * Template pools
 * -------------------------------------------------------------------------- */
/**
 * Which tokens each clause type's tuple can actually fill.
 *
 * This is the whole of M1-06's "no template references data the event tuple
 * doesn't carry" criterion, and it is checked at load rather than at render: a
 * declaration cannot name the part, because when a fighter swings nobody knows
 * yet where it lands, and a template that tried would print an empty hole into
 * a log three hours into a balance run.
 */
export const CLAUSE_TOKENS = {
    declaration: ['attacker', 'victim', 'weapon', 'their', 'them'],
    defence: ['attacker', 'victim', 'weapon', 'armour', 'part', 'their', 'them'],
    contact: ['attacker', 'victim', 'weapon', 'armour', 'their', 'them'],
    // Tissue templates live in the wound tables and are validated there, against
    // the full vocabulary. Listed for completeness of the lint's domain.
    tissue: NARRATION_TOKENS,
    status: ['attacker', 'victim', 'part', 'weapon', 'their', 'them'],
};
/** Minimum phrasings per pool, so a long fight does not read like a stuck record. */
export const MIN_POOL_TEMPLATES = 2;
export class NarrationError extends Error {
    constructor(message) {
        super(`narration: ${message}`);
        this.name = 'NarrationError';
    }
}
const TOKEN_PATTERN = /\{([a-zA-Z]+)\}/g;
/**
 * Validated, flattened template pools. Same shape of promise as `WoundTable`:
 * a hole fails on import, not on the ten-thousandth duel of an overnight run.
 */
export class NarrationPools {
    pools;
    constructor(pools) {
        this.pools = pools;
    }
    static from(source) {
        const pools = new Map();
        for (const clause of CLAUSE_TYPES) {
            const byKind = source[clause];
            if (!byKind)
                continue;
            for (const kind of Object.keys(byKind)) {
                const templates = byKind[kind];
                validatePool(clause, kind, templates);
                pools.set(poolKey(clause, kind), templates);
            }
        }
        if (pools.size === 0)
            throw new NarrationError('has no pools');
        return new NarrationPools(pools);
    }
    has(clause, kind) {
        return this.pools.has(poolKey(clause, kind));
    }
    /** Every pool defined, for the completeness lint. */
    get coverage() {
        return [...this.pools.keys()].map((key) => {
            const [clause, kind] = splitKey(key);
            return { clause, kind };
        });
    }
    /**
     * The phrasings for a cell. Throws rather than returning a bland default:
     * a missing pool means the world can do something no writer covered, and the
     * only useful moment to learn that is the first time it happens.
     */
    templates(clause, kind) {
        const pool = this.pools.get(poolKey(clause, kind));
        if (!pool)
            throw new NarrationError(`no ${clause} pool for '${kind}'`);
        return pool;
    }
}
function poolKey(clause, kind) {
    return `${clause}/${kind}`;
}
function splitKey(key) {
    const cut = key.indexOf('/');
    return [key.slice(0, cut), key.slice(cut + 1)];
}
function validatePool(clause, kind, templates) {
    const where = `${clause}/${kind}`;
    if (!Array.isArray(templates) || templates.length < MIN_POOL_TEMPLATES) {
        throw new NarrationError(`${where}: needs at least ${MIN_POOL_TEMPLATES} templates (got ${templates?.length ?? 0})`);
    }
    const allowed = CLAUSE_TOKENS[clause];
    for (const template of templates) {
        if (!template.trim())
            throw new NarrationError(`${where}: has an empty template`);
        for (const match of template.matchAll(TOKEN_PATTERN)) {
            const token = match[1];
            if (!NARRATION_TOKENS.includes(token)) {
                throw new NarrationError(`${where}: unknown token '{${token}}' in "${template}"`);
            }
            if (!allowed.includes(token)) {
                throw new NarrationError(`${where}: a ${clause} clause carries no '{${token}}' — in "${template}"`);
            }
        }
    }
}
export function fillTemplate(template, values) {
    return template.replace(TOKEN_PATTERN, (_whole, token) => {
        const value = values[token];
        if (value === undefined) {
            throw new NarrationError(`template "${template}" wants {${token}}, which this event has no`);
        }
        return value;
    });
}
/* -------------------------------------------------------------------------- *
 * Rendering a blow
 * -------------------------------------------------------------------------- */
/**
 * The status kinds a blow can report beyond the clock's own vocabulary: what
 * the wound *is*, as distinct from what it did to the character.
 */
export const WOUND_STATUS_KINDS = ['sever', 'break', 'blind', 'bleeds', 'bleeds-heavily'];
/** A wound bleeding at or above this rate reads as heavy rather than merely open. */
export const HEAVY_BLEED = 6;
/** Which status pool a clock event draws from. `null` for events not worth a line. */
export function statusKindOf(event) {
    switch (event.type) {
        case 'blood-stage': {
            const stage = event.refs[0];
            // `died` is already coming; two clauses for one moment reads as a stutter.
            return stage === 'dead' ? null : `blood-${stage}`;
        }
        case 'infection-stage':
            return `infection-${event.refs[2]}`;
        default:
            return event.type;
    }
}
/** Render one resolution as its ordered clause chain (AoD §12.1). */
export function narrateBlow(rng, pools, blow) {
    const part = blow.damage?.part ?? null;
    const values = substitutionsFor(blow, part);
    const clauses = [];
    const draw = (type, kind, refs) => {
        clauses.push({
            type,
            text: fillTemplate(rng.pick(pools.templates(type, kind)), values),
            refs,
        });
    };
    draw('declaration', blow.attack, [blow.attacker.id, blow.defender.id, blow.weapon]);
    if (blow.defence !== null) {
        draw('defence', blow.defence, [blow.defender.id, blow.defence]);
    }
    else {
        const weight = blow.damage?.level === 0 ? 'glancing' : 'solid';
        const kind = blow.armour === null ? weight : `${weight}-through`;
        draw('contact', kind, [blow.defender.id, ...(blow.armour === null ? [] : [blow.armour])]);
    }
    if (blow.damage && part) {
        // The anatomical ladder, straight off the wound-table cell that resolved:
        // the tissue a level reaches is a balance decision, not a writing one.
        clauses.push({
            type: 'tissue',
            text: fillTemplate(rng.pick(blow.damage.entry.narration), values),
            refs: [blow.defender.id, part.id, String(blow.damage.level)],
        });
    }
    for (const kind of statusKinds(blow)) {
        draw('status', kind, [blow.defender.id, kind]);
    }
    return clauses;
}
/**
 * Render a chain that has no attacker — infection turning, a wound bleeding
 * out, a fever breaking. The same renderer, because "combat is the richest
 * producer, not a special case" (AoD §12.1) is a rule about this module.
 */
export function narrateEvents(rng, pools, subject, events, partNames = {}) {
    const clauses = [];
    for (const event of events) {
        const kind = statusKindOf(event);
        if (kind === null)
            continue;
        const part = event.refs.map((ref) => partNames[ref]).find((name) => name !== undefined);
        clauses.push({
            type: 'status',
            text: fillTemplate(rng.pick(pools.templates('status', kind)), {
                victim: subject.name,
                attacker: subject.name,
                their: subject.pronouns.possessive,
                them: subject.pronouns.object,
                ...(part === undefined ? {} : { part }),
            }),
            refs: [subject.id, kind, ...event.refs],
        });
    }
    return clauses;
}
function substitutionsFor(blow, part) {
    return {
        attacker: blow.attacker.name,
        victim: blow.defender.name,
        weapon: blow.weapon,
        their: blow.defender.pronouns.possessive,
        them: blow.defender.pronouns.object,
        ...(part === null ? {} : { part: part.name }),
        ...(blow.armour === null ? {} : { armour: blow.armour }),
    };
}
/**
 * Status clauses, in the order a reader wants them: what the blow did to the
 * body, then what the body is doing about it, then what the character is doing.
 *
 * Terminal effects come from `WoundApplication.terminal`, which reports only
 * *changes* — a second axe blow to an already-severed arm does not announce the
 * severing twice.
 */
function statusKinds(blow) {
    const kinds = [];
    const application = blow.damage?.application ?? null;
    if (application) {
        for (const effect of application.terminal)
            kinds.push(effect);
        const { bleedRate } = application.wound;
        if (bleedRate > 0)
            kinds.push(bleedRate >= HEAVY_BLEED ? 'bleeds-heavily' : 'bleeds');
    }
    for (const event of blow.events) {
        const kind = statusKindOf(event);
        if (kind !== null)
            kinds.push(kind);
    }
    return kinds;
}
/* -------------------------------------------------------------------------- *
 * Verbosity (AoD §12.2)
 * -------------------------------------------------------------------------- */
/**
 * How loudly a chain deserves to be shown. The full chain is *always* archived
 * — this decides only what the live log prints, because an eight-man brawl
 * rendered in full scrolls unreadably and the reader loses the death in it.
 */
export const PROMINENCES = ['minor', 'notable', 'major'];
/** Wounds at or above this level always get their named chain. */
export const MAJOR_WOUND_LEVEL = 3;
const MAJOR_EVENTS = new Set([
    'died',
    'senseless',
    'knocked-down',
    'amputated',
    'infected',
]);
export function prominenceOf(blow) {
    const level = blow.damage?.level ?? 0;
    if (level >= MAJOR_WOUND_LEVEL)
        return 'major';
    if (blow.damage?.application?.terminal.length)
        return 'major';
    if (blow.events.some((event) => MAJOR_EVENTS.has(event.type)))
        return 'major';
    if (level > 0)
        return 'notable';
    return 'minor';
}
export function chainFor(blow, clauses) {
    return {
        prominence: prominenceOf(blow),
        actors: [blow.attacker.id, blow.defender.id],
        clauses,
    };
}
/**
 * The live log for one round: full chains for what matters, one aggregate line
 * for the rest.
 *
 * Everything filtered out is still in the archive — this is a view, and the
 * client's battle-view toggle is the same chains with no filter at all.
 */
export function filterLog(chains, options = {}) {
    const focus = new Set(options.focus ?? []);
    const floor = PROMINENCES.indexOf(options.floor ?? 'notable');
    const lines = [];
    let hidden = 0;
    const hiddenActors = new Set();
    for (const chain of chains) {
        const loud = PROMINENCES.indexOf(chain.prominence) >= floor;
        const mine = chain.actors.some((actor) => focus.has(actor));
        if (!loud && !mine) {
            hidden += 1;
            for (const actor of chain.actors)
                hiddenActors.add(actor);
            continue;
        }
        for (const clause of chain.clauses) {
            lines.push({ text: clause.text, aggregated: 1, refs: clause.refs });
        }
    }
    if (hidden > 0) {
        // Accurate, not atmospheric: everything aggregated here was turned aside or
        // came to a bruise, and a summary line that implied wounds would be the one
        // place in the log that says something the sim did not do.
        lines.push({
            text: hidden === 1
                ? 'Another blow is traded and comes to nothing much.'
                : `${countWord(hidden)} more blows are traded and come to nothing much.`,
            aggregated: hidden,
            refs: [...hiddenActors],
        });
    }
    return lines;
}
const COUNT_WORDS = [
    'No',
    'A single',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
];
function countWord(n) {
    return COUNT_WORDS[n] ?? 'A dozen';
}
/**
 * Assert the pools cover everything that can happen — computed from the
 * content, never from a maintained list, so it starts failing the day a
 * creature gains an attack profile or the clocks gain an event.
 *
 * Lives here rather than in a test file so `@annwn/content` can run it at load
 * time in a headless harness, where a missing pool would otherwise surface as
 * a thrown error mid-run.
 */
export function assertPoolsCover(pools, domain) {
    const missing = [];
    const check = (clause, kinds) => {
        for (const kind of kinds) {
            if (!pools.has(clause, kind))
                missing.push(`${clause}/${kind}`);
        }
    };
    check('declaration', domain.attacks);
    check('defence', domain.defences ?? DEFENCE_KINDS);
    check('contact', domain.contacts ?? CONTACT_KINDS);
    check('status', domain.statuses);
    if (missing.length > 0) {
        throw new NarrationError(`is missing pools: ${missing.join(', ')}`);
    }
}
/** Every status kind the clocks and the wound effects can produce. */
export function reachableStatusKinds() {
    return [
        ...WOUND_STATUS_KINDS,
        'staggered',
        'knocked-down',
        'senseless',
        'roused',
        'died',
        'blood-pale',
        'blood-failing',
        'blood-dying',
        'infected',
        'infection-fevered',
        'infection-gangrenous',
        'infection-septic',
        'infection-terminal',
        'infection-halted',
        'amputated',
        'fled',
    ];
}
/**
 * The best piece of armour over a zone — the one the clause names.
 *
 * Ranked by what the piece is worth *against this blow* when a type is given,
 * so the byrnie that turns a seax gets the credit and the wool cloak that the
 * axe-haft went through gets the blame. Ties go to the first piece listed,
 * which puts the outermost layer in the sentence — the one an onlooker would
 * actually name.
 *
 * A piece worth nothing still wins over no piece at all. That is the clothing
 * layer: a wool tunic has no armour value and is never what stopped a blow, but
 * *"the blade goes through his tunic and into the shoulder"* is the line the
 * whole layer exists for.
 */
export function armourAt(armour, zone, type) {
    if (!armour)
        return null;
    let best = null;
    let bestValue = -1;
    for (const piece of armour) {
        if (!piece.covers.includes(zone))
            continue;
        const value = effectiveArmourValue(piece, type);
        if (value > bestValue) {
            best = piece;
            bestValue = value;
        }
    }
    return best;
}
//# sourceMappingURL=narrate.js.map